import { WarningCircleIcon } from '@phosphor-icons/react';
import React from 'react';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';

interface NumberInputProps<T extends FieldValues> extends React.ComponentProps<'input'> {
  leftIcon?: React.ReactNode;
  disabled?: boolean;
  placeholder: string;
  helperText?: string;
  error?: string;
  setValue: UseFormSetValue<T>;
  name: string;
}

export function NumberInput<T extends FieldValues>({
  leftIcon,
  disabled,
  placeholder,
  helperText,
  error,
  setValue,
  name,
  ...props
}: NumberInputProps<T>) {
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setValue(name as Path<T>, parseInt(value) as PathValue<T, Path<T>>);
  }

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
      <div
        className={`self-stretch px-3.5 py-2.5 rounded-lg outline-1 outline-offset-[-1px] inline-flex justify-start items-center gap-2 overflow-hidden input-shadow transition-colors ${
          disabled ? 'bg-gray-50' : 'bg-white'
        } ${
          error
            ? 'input-error outline-error-300 focus-within:outline-error-300'
            : 'outline-gray-300 focus-within:outline-blue-300'
        }`}
      >
        <div className="flex-1 flex justify-start items-center gap-2">
          {leftIcon && <div className="text-gray-500">{leftIcon}</div>}
          <input
            {...props}
            className="flex-1 justify-start placeholder:text-gray-500 text-gray-900 text-base font-normal leading-normal outline-none"
            placeholder={placeholder}
            type="number"
            onChange={(e) => {
              if (props.onChange) {
                props.onChange(e);
              }
              handleChange(e);
            }}
          />
          {error && <WarningCircleIcon className="text-error-500" />}
        </div>
      </div>
      {error ? (
        <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
          {error}
        </div>
      ) : (
        <div className="self-stretch justify-start text-gray-600 text-xs font-normal leading-none">
          {helperText}
        </div>
      )}
    </div>
  );
}
